import type { Fact } from "../../common/fact-table";
import { aspectRatio, formatBytes } from "./container";
import type { Exif, IfdName } from "./exif";
import { findEntry } from "./exif";
import type { Loaded } from "./source";
import { coordinate, degreesMinutesSeconds, plainText, tagName, tagText } from "./tags";

// What the file says, in the order somebody reads it: the file itself, then the picture, then what the camera
// recorded, then whatever else was written into it.

export interface Group {
  title: string;
  rows: Fact[];
}

export function fileFacts(loaded: Loaded): Fact[] {
  const { info, element } = loaded;
  const shown = { width: element.naturalWidth, height: element.naturalHeight };
  const stored = info.width > 0 ? info : shown;

  return [
    { label: "Name", value: loaded.name },
    { label: "Type", value: `${info.label} (${loaded.type})` },
    { label: "Size", value: formatBytes(loaded.size) },
    { label: "Modified", value: loaded.modified ? new Date(loaded.modified).toLocaleString() : "" },
    { label: "Dimensions", value: `${shown.width} × ${shown.height}` },
    // A camera writes the sensor's own reading and an orientation tag beside it, so the two disagree by a quarter
    // turn on most photographs taken sideways; every viewer shows the second, and this is where it is said out loud.
    {
      label: "Stored as",
      value: stored.width === shown.width && stored.height === shown.height
        ? ""
        : `${stored.width} × ${stored.height}, turned by the orientation tag`,
    },
    { label: "Aspect ratio", value: aspectRatio(shown.width, shown.height) },
    { label: "Megapixels", value: megapixels(shown.width, shown.height) },
    { label: "Transparency", value: info.hasAlpha ? "Yes" : "" },
    { label: "Animated", value: info.animated ? "Yes" : "" },
    ...info.facts,
  ];
}

// Every entry the file carries, grouped by the directory it sits in, which is the difference between a camera's own
// reading and the picture's shape and is worth keeping apart.
export function exifGroups(exif: Exif | null): Group[] {
  if (!exif) return [];
  const groups: Group[] = [];
  for (const [name, title] of Object.entries(IFD_TITLES) as [IfdName, string][]) {
    const entries = exif.ifds[name];
    if (entries.length === 0) continue;
    groups.push({
      title,
      rows: entries.map((entry) => ({ label: tagName(name, entry.tag), value: tagText(name, entry, exif.little) })),
    });
  }
  return groups;
}

// The one reading nothing else on the page gives: three rationals and a letter are a place, and a place is the
// single thing in a photograph's metadata somebody most often did not know was in it.
export function locationFacts(exif: Exif | null): Fact[] {
  if (!exif || exif.ifds.gps.length === 0) return [];
  const latitude = coordinate(findEntry(exif.ifds.gps, 2)?.value ?? [], text(exif, 1));
  const longitude = coordinate(findEntry(exif.ifds.gps, 4)?.value ?? [], text(exif, 3));
  if (latitude === null || longitude === null) return [];
  return [
    { label: "Latitude", value: `${latitude.toFixed(6)}  (${degreesMinutesSeconds(latitude, "lat")})` },
    { label: "Longitude", value: `${longitude.toFixed(6)}  (${degreesMinutesSeconds(longitude, "lon")})` },
    { label: "Decimal pair", value: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` },
  ];
}

// The notes a container keeps outside its EXIF, which is where most of what a screenshot or an export carries lives.
export function otherFacts(loaded: Loaded): Fact[] {
  const rows: Fact[] = loaded.info.text.map((entry) => ({ label: entry.key, value: entry.value }));
  for (const [at, comment] of loaded.info.comments.entries()) {
    rows.push({ label: loaded.info.comments.length > 1 ? `Comment ${at + 1}` : "Comment", value: comment.trim() });
  }
  if (loaded.info.xmp) rows.push({ label: "XMP", value: `${loaded.info.xmp.length} characters` });
  return rows;
}

function megapixels(width: number, height: number): string {
  const total = (width * height) / 1e6;
  if (total <= 0) return "";
  return `${total < 1 ? total.toFixed(2) : total.toFixed(1)} MP`;
}

function text(exif: Exif, tag: number): string {
  return plainText(findEntry(exif.ifds.gps, tag)?.value ?? "");
}

const IFD_TITLES: Record<string, string> = {
  image: "Image",
  exif: "Camera",
  gps: "Location",
  interop: "Interoperability",
  thumbnail: "Thumbnail",
};
